import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import SignaturePad from "@/dealership/components/SignaturePad";
import { PenLine, Type, X } from "lucide-react";

const DIMS = {
  signature: { w: 190, imgH: 40 },
  initial: { w: 88, imgH: 34 },
};

/**
 * A single fillable signing spot rendered over the Bill of Sale. Clicking it
 * opens a capture dialog; the saved image prints in place above a labelled line.
 */
export default function SignSpot({ spot, signer, value, onSign, disabled, autofillImage }) {
  const [open, setOpen] = useState(false);
  const dim = DIMS[spot.type] || DIMS.signature;
  const isInitial = spot.type === "initial";
  const color = signer?.color || "#334155";
  const signed = !!value?.image;
  const testid = `sign-spot-${spot.id}`;

  return (
    <div
      className="absolute pointer-events-auto"
      style={{ left: `${spot.x * 100}%`, top: `${spot.y * 100}%`, transform: "translate(-50%, -50%)", width: dim.w }}
      data-testid={testid}
    >
      {/* image / trigger area */}
      <div className="relative flex items-end justify-center" style={{ height: dim.imgH }}>
        {signed ? (
          <>
            <img src={value.image} alt={spot.label} className="max-h-full object-contain" />
            {!disabled && (
              <button
                onClick={() => onSign("")}
                className="no-print absolute -right-2 -top-2 w-4 h-4 rounded-full bg-white border border-zinc-300 text-zinc-500 hover:text-red-500 flex items-center justify-center shadow-sm"
                title="Clear" data-testid={`${testid}-clear`}
              >
                <X className="h-2.5 w-2.5" />
              </button>
            )}
          </>
        ) : autofillImage ? (
          <>
            <img src={autofillImage} alt={spot.label} className="max-h-full object-contain" data-testid={`${testid}-autofill`} />
            {!disabled && (
              <button
                onClick={() => setOpen(true)}
                className="no-print absolute -right-2 -top-2 w-4 h-4 rounded-full bg-white border border-zinc-300 text-zinc-500 hover:text-emerald-600 flex items-center justify-center shadow-sm"
                title="Sign in person instead" data-testid={`${testid}-resign`}
              >
                <PenLine className="h-2.5 w-2.5" />
              </button>
            )}
          </>
        ) : (
          !disabled && (
            <button
              onClick={() => setOpen(true)}
              className="no-print w-full h-full rounded-md border border-dashed flex items-center justify-center gap-1 text-[9px] font-semibold uppercase tracking-wide transition-colors hover:bg-black/[0.03]"
              style={{ borderColor: color, color }}
              data-testid={`${testid}-open`}
            >
              {isInitial ? <Type className="h-3 w-3" /> : <PenLine className="h-3 w-3" />}
              {isInitial ? "Initial" : "Sign"}
            </button>
          )
        )}
      </div>

      {/* line + label */}
      <div className="border-t border-zinc-900 pt-0.5 mt-0.5 flex items-center justify-between gap-1 leading-none">
        <span className="text-[7.5px] uppercase text-zinc-600 truncate">
          {signer?.label || "Signer"} · {spot.label || (isInitial ? "Initials" : "Signature")}
        </span>
        {value?.date && <span className="text-[7.5px] font-mono-data text-zinc-500 shrink-0">{value.date}</span>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid={`${testid}-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {isInitial ? <Type className="h-4 w-4" /> : <PenLine className="h-4 w-4" />}
              {isInitial ? "Initials" : "Signature"} — {signer?.label || "Signer"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground -mt-1">{spot.label}</p>
          <SignaturePad
            label={signer?.label || "Signer"}
            onSave={(img) => { if (img) { onSign(img); setOpen(false); } }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
